import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Which panel the request comes from: everything outside /admin/* is the
    // organization panel. Only matters for a super_admin who also belongs to
    // an organization — the backend then scopes them to that organization
    // (organizations/permissions.py is_platform_scope); ignored for everyone else.
    if (!window.location.pathname.startsWith('/admin')) {
      config.headers['X-Panel-Mode'] = 'org';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle token refresh
api.interceptors.response.use(
  (response) => response.data,  // Return only data, not full response
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, try refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_BASE_URL}/api/users/auth/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = response.data.access;  // axios.post doesn't use our interceptor
        localStorage.setItem('access_token', newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API Endpoints
export const endpoints = {
  // Auth
  auth: {
    logout: '/api/users/auth/logout/',
    refresh: '/api/users/auth/refresh/',
    me: '/api/users/auth/me/',
    // Job title / phones / office as the org's directory (Microsoft 365) has them.
    meDirectory: '/api/users/auth/me/directory/',
    // Microsoft SSO login (organizations/sso_login.py) — distinct from
    // organizations.graphConsentUrl below, which connects an org's
    // directory for signature distribution, not a user login.
    ssoMicrosoftLoginUrl: '/api/organizations/sso/microsoft/login-url/',
    ssoMicrosoftExchange: '/api/organizations/sso/microsoft/exchange/',
  },

  // Exchange Setup (Public)
  exchange: {
    register: '/api/exchange/register/',
    // Public landing-page "Demo planlayın" form (exchange_setup.DemoRequest).
    demoRequest: '/api/exchange/demo-request/',
    mySetupStatus: '/api/exchange/my-setup-status/',
  },

  // Organizations
  organizations: {
    domains: '/api/organizations/domains/',
    graphConsentUrl: '/api/organizations/graph/consent-url/',
    graphStatus: '/api/organizations/graph/status/',
    graphDisconnect: '/api/organizations/graph/disconnect/',
    addinMarkDeployed: '/api/organizations/addin/mark-deployed/',
    googleConsentUrl: '/api/organizations/google/consent-url/',
    googleStatus: '/api/organizations/google/status/',
    googleDisconnect: '/api/organizations/google/disconnect/',
    gmailAddonMarkDeployed: '/api/organizations/gmail-addon/mark-deployed/',
  },

  // Team invites
  invites: {
    list: '/api/organizations/invites/',
    create: '/api/organizations/invites/',
    revoke: (id) => `/api/organizations/invites/${id}/revoke/`,
    resend: (id) => `/api/organizations/invites/${id}/resend/`,
    lookup: (token) => `/api/organizations/invites/lookup/?token=${encodeURIComponent(token)}`,
  },

  // Billing (PAYTR)
  billing: {
    plans: '/api/billing/plans/',
    subscription: '/api/billing/subscription/',
    profile: '/api/billing/profile/',
    quote: '/api/billing/quote/',
    autoRenew: '/api/billing/auto-renew/',
    startPayment: '/api/billing/start-payment/',
    orderStatus: (merchantOid) => `/api/billing/order-status/${merchantOid}/`,
  },

  // Users
  users: '/api/users/',

  // Admin - Organizations
  admin: {
    // Admin - Registrations (NEW)
    registrations: {
      list: '/api/exchange/admin/registrations/',
      detail: (id) => `/api/exchange/admin/registrations/${id}/`,
      approve: (id) => `/api/exchange/admin/registrations/${id}/approve/`,
      reject: (id) => `/api/exchange/admin/registrations/${id}/reject/`,
      schedule: (id) => `/api/exchange/admin/registrations/${id}/schedule/`,
    },

    organizations: {
      list: '/api/admin/organizations/',
      detail: (id) => `/api/admin/organizations/${id}/`,
      stats: (id) => `/api/admin/organizations/${id}/stats/`,
    },

    // Admin - Users
    users: {
      list: '/api/admin/users/',
      detail: (id) => `/api/admin/users/${id}/`,
      changeRole: (id) => `/api/admin/users/${id}/change_role/`,
      activate: (id) => `/api/admin/users/${id}/activate/`,
      deactivate: (id) => `/api/admin/users/${id}/deactivate/`,
    },

    // Admin - Licenses
    licenses: {
      list: '/api/admin/licenses/',
      detail: (id) => `/api/admin/licenses/${id}/`,
    },

    // Admin - Analytics
    analytics: {
      overview: '/api/admin/analytics/overview/',
      orgStats: (id) => `/api/admin/analytics/organizations/${id}/`,
    },

    // Admin - Audit Log (read-only)
    auditLog: {
      list: '/api/admin/audit-log/',
    },
    demoRequests: {
      list: '/api/exchange/admin/demo-requests/',
      update: (id) => `/api/exchange/admin/demo-requests/${id}/`,
    },

    // Admin - Notifications
    notifications: {
      list: '/api/admin/notifications/',
      detail: (id) => `/api/admin/notifications/${id}/`,
      create: '/api/admin/notifications/',
      user: '/api/admin/notifications/user/',
      markRead: (id) => `/api/admin/notifications/${id}/read/`,
    },
  },

  // Signatures
  signatures: {
    // The signed-in user's own template/language pick ("İmzam" page).
    mySignature: '/api/signatures/me/signature/',
    mySignaturePreview: '/api/signatures/me/signature/preview/',
    templates: {
      list: '/api/signatures/templates/',
      detail: (id) => `/api/signatures/templates/${id}/`,
      create: '/api/signatures/templates/create/',
    },
    groups: {
      list: '/api/signatures/groups/',
      visibility: '/api/signatures/groups/visibility/',
    },
    userSignatures: '/api/signatures/user-list/',
    dashboard: {
      stats: '/api/signatures/dashboard/stats/',
    },
    deliveryLog: {
      list: '/api/signatures/delivery-log/',
      stats: '/api/signatures/delivery-log/stats/',
      addinUsage: '/api/signatures/delivery-log/addin-usage/',
    },
    alerts: '/api/signatures/alerts/',
    alertDetail: (id) => `/api/signatures/alerts/${id}/`,
    userTemplate: '/api/signatures/user-template/',
    userOverride: '/api/signatures/user-override/',
    userSelectableTemplates: '/api/signatures/user-selectable-templates/',
    brandAssets: {
      list: '/api/signatures/brand-assets/',
      detail: (id) => `/api/signatures/brand-assets/${id}/`,
    },
    brandColors: {
      list: '/api/signatures/brand-colors/',
      detail: (id) => `/api/signatures/brand-colors/${id}/`,
    },
    disclaimers: {
      list: '/api/signatures/disclaimers/',
      create: '/api/signatures/disclaimers/',
      detail: (id) => `/api/signatures/disclaimers/${id}/`,
    },
    campaigns: {
      list: '/api/signatures/campaigns/',
      create: '/api/signatures/campaigns/',
      detail: (id) => `/api/signatures/campaigns/${id}/`,
      analytics: (id) => `/api/signatures/campaigns/${id}/analytics/`,
    },
    surveys: {
      list: '/api/signatures/surveys/',
      analytics: (id) => `/api/signatures/surveys/${id}/analytics/`,
    },
    surveyFeedbackActions: {
      list: '/api/signatures/survey-feedback-actions/',
      detail: (id) => `/api/signatures/survey-feedback-actions/${id}/`,
    },
    previewPlaceholders: '/api/signatures/preview-placeholders/',
    addinSettings: '/api/signatures/addin-settings/',
  },
};

export { api };
export default api;
